import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import jsPDF from 'jspdf';
import dayjs from "dayjs";
import autoTable from 'jspdf-autotable';
import PropTypes from 'prop-types';
import { 
  Alert,
  Avatar,
  Button,
  Chip,
  Skeleton,
  Stack,
  IconButton,
  DialogContent,
  DialogTitle,
  Dialog as MuiDialog
} from '@mui/material';
import { Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableFooter, TablePagination, TableRow, Paper, Box } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { formatServiceLevelSummary } from '../../../../libs/labels';

const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

export const normalizeInvoice = (id, data = {}) => ({
  id,
  ...data,
  invoiceId: readField(data, 'invoice_id', id),
  timesheetId: readField(data, 'timesheet_id', ''),
  userId: readField(data, 'user_id', ''),
  hoursWorked: readField(data, 'hours_worked', 0),
  createdAt: readField(data, 'created_at', null),
  dueDate: readField(data, 'due_date', ''),
  updatedAt: readField(data, 'updated_at', null),
});

const formatAddress = (address) => {
  if (!address) return '';
  const { line1, line2, city, state, postal_code, country } = address;
  return [line1, line2, city, state, postal_code, country].filter(Boolean).join(', ');
};

const InvoiceTemplate = ({ invoice }) => {
  if (!invoice) return null;

  const {
    invoiceId,
    createdAt,
    dueDate,
    seller,
    buyer,
    entries,
    metadata,
    amount,
    hoursWorked,
    month
  } = invoice;
  const serviceSummary = formatServiceLevelSummary(metadata?.service, metadata?.level);

  const downloadPDF = () => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const padding = 40;

    doc.setFontSize(20);
    doc.text('Invoice', padding, 50);

    doc.setFontSize(10);
    autoTable(doc, {
      startY: 70,
      margin: { left: padding },
      columnStyles: { 0: { fontStyle: 'bold', minCellWidth: 80, cellWidth: 80, cellPadding: 0 }, 1: { halign: 'left', cellPadding: 0 }  },
      body: [
        ['Invoice Id', `${invoiceId}`],
        ['Date of issue', `${new Date(createdAt?.seconds * 1000).toDateString()}`],
        ['Due Date', `${new Date(dueDate).toDateString()}`],
      ],
      theme: 'plain',
      tableWidth: 300
    })

    doc.text(`From:`, padding, 135);
    doc.text(seller.name, padding, 150);
    doc.text(formatAddress(seller.address), padding, 162, {maxWidth: 200});

    doc.text(`Bill to:`, 320, 135);
    doc.text(buyer.name, 320, 150);
    doc.text(formatAddress(buyer.address), 320, 162, {maxWidth: 200});

    autoTable(doc, {
      startY: 200,
      margin: { left: padding },
      columnStyles: { 0: { fontStyle: 'bold', minCellWidth: 80, cellWidth: 80, cellPadding: 0 }, 1: { halign: 'left', cellPadding: 0 }  },
      body: [
        ['Month', `${dayjs(month, 'YYYY-MM').format('MMMM YYYY')}`],
        ['Service', serviceSummary],
      ],
      theme: 'plain',
      tableWidth: 200
    })

    const headers = [['#', 'Date', 'Hours', 'Rate', 'Amount']];
    const data = entries.map((entry, i) => [
      i + 1,
      entry.date,
      entry.hours,
      `$${(entry.rate / 100).toFixed(2)}`,
      `$${((entry.hours * entry.rate) / 100).toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: 245,
      head: headers,
      body: data,
      margin: { left: padding },
      theme: 'grid',
      styles: { fontSize: 10 }
    });

    autoTable(doc, {
      margin: { left: doc.internal.pageSize.getWidth() - 280, top: 10 },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' } },
      body: [
        ['Total hours', `${hoursWorked}h`],
        ['Subtotal', `$${(amount/100).toFixed(2)}`],
        ['Total', `$${(amount/100).toFixed(2)}`],
        ['Amount due', `$${(amount/100).toFixed(2)}`],
      ],
      theme: 'grid',
      tableWidth: 240,
      didDrawPage: function (data) {
        doc.text(`${invoiceId} - $${(amount/100).toFixed(2)} due ${new Date(dueDate).toDateString()}`, 40, doc.internal.pageSize.height - 40);
      }
    })

    doc.save(`${invoiceId}.pdf`);
  };

  return (
    <Box className="bg-white p-6 rounded-2xl shadow-md" sx={{ maxWidth: '600px', margin: '0 auto' }}>
      <Grid container justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Grid item>
          {/* <Typography variant="h5" fontWeight="bold">Invoice</Typography> */}
          <Typography variant="body2"><strong>Invoice ID:</strong> {invoiceId}</Typography>
          <Typography variant="body2"><strong>Date of issue:</strong> {new Date(createdAt?.seconds * 1000).toDateString()}</Typography>
          <Typography variant="body2"><strong>Due Date:</strong> {new Date(dueDate).toDateString()}</Typography>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={downloadPDF}><DownloadIcon/></Button>
        </Grid>
      </Grid>

      <Grid container spacing={4} mb={4}>
        <Grid item xs={6}>
          <Typography variant="subtitle1" fontWeight="bold">From</Typography>
          <Typography variant="body2">{seller.name}</Typography>
          <Typography variant="body2">{formatAddress(seller.address)}</Typography>
        </Grid>
        <Grid item xs={6} >
          <Typography variant="subtitle1" fontWeight="bold">Bill to</Typography>
          <Typography variant="body2">{buyer.name}</Typography>
          <Typography variant="body2">{formatAddress(buyer.address)}</Typography>
        </Grid>
      </Grid>

      <Box mb={4}>
        <Typography variant="body2" style={{ fontStyle: 'normal'}}><strong>Month:</strong> {dayjs(month, 'YYYY-MM').format('MMMM YYYY')}</Typography>
        <Typography variant="body2" style={{ fontStyle: 'normal'}}><strong>Service:</strong> {serviceSummary}</Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f9fafb' }}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{entry.date}</TableCell>
                <TableCell>{entry.hours}</TableCell>
                <TableCell>${(entry.rate / 100).toFixed(2)}</TableCell>
                <TableCell>${((entry.hours * entry.rate) / 100).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={4} textAlign="right">
        <Stack sx={{ p: 0, display: 'flex', textAlign: 'left'}}>
          <Typography variant="body2">Total Hours: <strong>{hoursWorked}h</strong></Typography>
          <Typography variant="body2">Total Amount: <strong>${(amount/100).toFixed(2)}</strong></Typography>
        </Stack>
      </Box>
    </Box>
  );
};

const TablePaginationActions = (props) => {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;
  
    const handleFirstPageButtonClick = (event) => {
      onPageChange(event, 0);
    };
  
    const handleBackButtonClick = (event) => {
      onPageChange(event, page - 1);
    };
  
    const handleNextButtonClick = (event) => {
      onPageChange(event, page + 1);
    };
  
    const handleLastPageButtonClick = (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };
  
    return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    );
}
  
TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
  };

const InvoiceList = ({ embedded = false, invoices: invoicesProp, loading = false }) => {
  const theme = useTheme();
  const userId = FirebaseAuth.auth().currentUser.uid
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [localInvoices, setLocalInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const invoices = Array.isArray(invoicesProp) ? invoicesProp : localInvoices;

  const paidInvoices = invoices.filter((invoice) => String(invoice.status || "").toLowerCase() === "paid");
  const pendingInvoices = invoices.filter((invoice) => String(invoice.status || "").toLowerCase() !== "paid");
  const filteredInvoices = invoiceFilter === 'paid' ? paidInvoices : invoiceFilter === 'pending' ? pendingInvoices : invoices;
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredInvoices.length) : 0;
  const statusChipColor = (status) => (String(status || "").toLowerCase() === "paid" ? "success" : "warning");
  const formatCurrency = (amountInCents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((amountInCents || 0) / 100);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectFilter = (next) => {
    setInvoiceFilter(next);
    setPage(0);
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      const querySnapshot = await FirebaseAuth.firestore().collection('users').doc(FirebaseAuth.auth().currentUser.uid).collection('invoices').orderBy('month', 'desc').get();
      const list = querySnapshot.docs.map(doc => normalizeInvoice(doc.id, doc.data() || {}));
      setLocalInvoices(list);
    };

    if (!Array.isArray(invoicesProp) && userId) fetchInvoices();
  }, [invoicesProp, userId]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        mb: embedded ? 0 : 2,
        display: 'block',
        borderRadius: embedded ? 0 : '8px',
        borderWidth: embedded ? 0 : '1px',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {!embedded && (
        <>
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar variant="rounded" sx={{ height: '32px', width: '32px', bgcolor: theme.palette.grey[700] }}>
                  <ReceiptIcon fontSize="small" />
                </Avatar>
                <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                  Invoices
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ px: 2, pb: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Paper
                variant="outlined"
                onClick={() => selectFilter('all')}
                sx={{ p: 1.5, borderRadius: 1.5, flex: 1, cursor: 'pointer', borderColor: invoiceFilter === 'all' ? 'primary.main' : 'divider', bgcolor: invoiceFilter === 'all' ? '#eef2ff' : 'transparent' }}
              >
                <Typography variant="caption" color="text.secondary">Total invoices</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{invoices.length}</Typography>
              </Paper>
              <Paper
                variant="outlined"
                onClick={() => selectFilter('pending')}
                sx={{ p: 1.5, borderRadius: 1.5, flex: 1, cursor: 'pointer', borderColor: invoiceFilter === 'pending' ? 'primary.main' : 'divider', bgcolor: invoiceFilter === 'pending' ? '#eef2ff' : 'transparent' }}
              >
                <Typography variant="caption" color="text.secondary">Open</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{pendingInvoices.length}</Typography>
              </Paper>
              <Paper
                variant="outlined"
                onClick={() => selectFilter('paid')}
                sx={{ p: 1.5, borderRadius: 1.5, flex: 1, cursor: 'pointer', borderColor: invoiceFilter === 'paid' ? 'primary.main' : 'divider', bgcolor: invoiceFilter === 'paid' ? '#eef2ff' : 'transparent' }}
              >
                <Typography variant="caption" color="text.secondary">Paid</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{paidInvoices.length}</Typography>
              </Paper>
            </Stack>
          </Box>
        </>
      )}

      <Box sx={{ px: embedded ? 0 : 2, pb: embedded ? 0 : 2 }}>
        <TableContainer component={Paper} variant="outlined" sx={{ border: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Invoice ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ minHeight: embedded ? '220px' : '300px' }}>
              {loading && (
                Array.from({ length: 4 }).map((_, index) => (
                  <TableRow key={`invoice-skeleton-${index}`}>
                    <TableCell><Skeleton width="70%" /></TableCell>
                    <TableCell><Skeleton width="85%" /></TableCell>
                    <TableCell><Skeleton width={72} /></TableCell>
                    <TableCell align="right"><Skeleton width={80} sx={{ ml: "auto" }} /></TableCell>
                  </TableRow>
                ))
              )}

              {!loading && (rowsPerPage > 0
                ? filteredInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : filteredInvoices
              ).map((invoice) => (
                <TableRow
                  key={invoice.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => { setSelectedInvoice(invoice); setOpenDialog(true); }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {dayjs(invoice.month, 'YYYY-MM').format('MMMM YYYY')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {invoice.invoiceId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={String(invoice.status || "pending").toLowerCase()}
                      color={statusChipColor(invoice.status)}
                      sx={{ borderRadius: 1, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(invoice.amount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              {!loading && emptyRows > 0 && (
                <TableRow style={{ height: 56 * emptyRows }}>
                  <TableCell colSpan={4} />
                </TableRow>
              )}

              {!loading && filteredInvoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Alert severity="info" sx={{ width: '100%', margin: "0 auto" }}>
                      No invoices found.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!loading && filteredInvoices.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                    colSpan={4}
                    count={filteredInvoices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    slotProps={{
                      select: {
                        inputProps: {
                          'aria-label': 'invoices per page',
                        },
                        native: true,
                      },
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>

        <MuiDialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle sx={{ display: 'flex' }}>
            <Stack spacing={0.5}>
              <Typography variant="h5" fontWeight="bold">Invoice</Typography>
            </Stack>
            <IconButton edge="end" sx={{ ml: 'auto' }} onClick={() => setOpenDialog(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedInvoice && <InvoiceTemplate invoice={selectedInvoice} />}
          </DialogContent>
        </MuiDialog>
      </Box>
    </Paper>
  );
};

export default InvoiceList;
